/**
 * Job applications — prepare the client for a specific job posting (no jobs database:
 * the adviser pastes a real posting).
 *
 * Per posting: Fit (match score, what matches, what's missing, what to ask the client) →
 * Tailored resume (same facts, re-ordered for this job) → Cover letter → Interview prep.
 * Each is its own explicit AI step (token-billed), fully editable, with a live PDF
 * preview, download and save-to-case-files. Status tracks where the application stands.
 *
 * Backend: /resumes/case/:threadId/jobs/* on maven-platform-server.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../../../auth/AuthenticatedAxios';
import { useToast } from '../../../../Context/ToastContext';
import { useConfirm } from '../../../../Context/ConfirmContext';
import {
  Row, Field, Label, Hint, Input, TextArea, Select, Btn, IconBtn, Banner, Entry, Iframe, PreviewEmpty,
} from './resumeStyles';
import {
  Section, SummaryEditor, SkillsEditor, ExperienceEditor, EducationEditor, CertificationsEditor,
  LanguagesEditor, AdditionalEditor, BulletsEditor,
} from './ResumeSectionEditors';
import { errMsg, isTokenError, downloadPdf, usePdfPreview, fmtDateTime } from './resumeUtils';

const STATUSES = [
  ['preparing', 'Preparing'], ['applied', 'Applied'], ['interview', 'Interview'], ['offer', 'Offer'],
  ['hired', 'Hired'], ['not_selected', 'Not selected'], ['withdrawn', 'Withdrawn'],
];
const VERDICT = {
  strong: { label: 'Strong fit', color: '#22c55e' }, good: { label: 'Good fit', color: '#84cc16' },
  partial: { label: 'Partial fit', color: '#f59e0b' }, weak: { label: 'Weak fit', color: '#ef4444' },
};
const SEVERITY = { dealbreaker: ['Deal-breaker', '#fca5a5'], important: ['Important', '#fcd34d'], nice: ['Nice to have', '#93c5fd'] };

const Layout = styled.div`
  display: grid; grid-template-columns: 290px minmax(0, 1fr); height: 100%; min-height: 0;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;
const ListCol = styled.div`border-right: 1px solid rgba(120,140,180,0.12); overflow-y: auto; padding: 16px;`;
const DetailCol = styled.div`overflow-y: auto; padding: 16px 18px; min-height: 0;`;
const JobItem = styled.button`
  width: 100%; text-align: left; cursor: pointer; padding: 10px 12px; border-radius: 10px; margin-bottom: 8px;
  border: 1px solid ${p => (p.$sel ? 'rgba(139,92,246,0.6)' : 'rgba(120,140,180,0.15)')};
  background: ${p => (p.$sel ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.02)')}; color: #e6e9f0;
  .t { font-size: 13.5px; font-weight: 700; } .s { font-size: 12px; color: #8b93a7; margin-top: 2px; }
`;
const SubTabs = styled.div`display: flex; gap: 6px; flex-wrap: wrap; margin: 14px 0;`;
const SubTab = styled.button`
  padding: 7px 13px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600;
  border: 1px solid ${p => (p.$active ? 'rgba(139,92,246,0.6)' : 'rgba(120,140,180,0.18)')};
  background: ${p => (p.$active ? 'rgba(139,92,246,0.18)' : 'transparent')}; color: ${p => (p.$active ? '#fff' : '#aeb6ca')};
  .dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #22c55e; margin-left: 6px; vertical-align: middle; }
`;
const Split = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; align-items: start;
  @media (max-width: 1300px) { grid-template-columns: 1fr; }
`;
const PreviewBox = styled.div`position: sticky; top: 0; display: flex; flex-direction: column; gap: 8px; height: 78vh;`;
const Score = styled.div`
  width: 74px; height: 74px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  font-size: 22px; font-weight: 800; color: #fff;
  background: conic-gradient(${p => p.$c} ${p => p.$v * 3.6}deg, rgba(255,255,255,0.08) 0deg);
  span { width: 60px; height: 60px; border-radius: 50%; background: #121826; display: flex; align-items: center; justify-content: center; }
`;

const jobLabel = (j) => j.title || j.company ? [j.title, j.company].filter(Boolean).join(' — ') : 'Untitled posting';

// ============================================================================

export default function JobApplications({ threadId, hasSavedResume, clientName, onAskClient, onEmail }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const [jobs, setJobs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ postingText: '', sourceUrl: '', title: '', company: '', language: 'en' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (selectId) => {
    try {
      const r = await api.get(`/resumes/case/${threadId}/jobs`);
      const list = r.data.data.jobs || [];
      setJobs(list);
      setSelected(prev => selectId || (list.some(j => j.id === prev) ? prev : list[0]?.id || null));
      if (!list.length) setAdding(true);
    } catch (e) { setJobs([]); showToast(errMsg(e, 'Could not load applications'), 'error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (draft.postingText.trim().length < 80) return showToast('Paste the full job posting', 'warning');
    setBusy(true);
    try {
      const r = await api.post(`/resumes/case/${threadId}/jobs`, draft);
      const job = r.data.data.job;
      setDraft({ postingText: '', sourceUrl: '', title: '', company: '', language: draft.language });
      setAdding(false);
      await load(job.id);
      // Fit analysis right away — it's the first thing the adviser wants to see.
      if (hasSavedResume) {
        try {
          const a = await api.post(`/resumes/case/${threadId}/jobs/${job.id}/analyze`);
          replaceJob(a.data.data.job);
        } catch (e) { if (!isTokenError(e)) showToast(errMsg(e, 'Fit analysis failed — try again from the Fit tab'), 'error'); }
      }
    } catch (e) { showToast(errMsg(e, 'Could not add the posting'), 'error'); }
    finally { setBusy(false); }
  };

  const replaceJob = (job) => setJobs(list => (list || []).map(j => (j.id === job.id ? job : j)));
  const current = (jobs || []).find(j => j.id === selected);

  return (
    <Layout>
      <ListCol>
        <Btn style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }} onClick={() => setAdding(true)}>+ Add a job posting</Btn>
        {jobs === null && <Hint>Loading…</Hint>}
        {(jobs || []).map(j => (
          <JobItem key={j.id} $sel={!adding && j.id === selected} onClick={() => { setSelected(j.id); setAdding(false); }}>
            <div className="t">{jobLabel(j)}</div>
            <div className="s">
              {j.fit ? `${j.fit.score}% · ` : ''}{(STATUSES.find(s => s[0] === j.status) || [])[1]} · {fmtDateTime(j.createdAt)}
            </div>
          </JobItem>
        ))}
      </ListCol>

      <DetailCol>
        {!hasSavedResume && <Banner $tone="warn">Save the client's resume first (Resume tab) — every application is built from it.</Banner>}
        {adding ? (
          <>
            <Label style={{ fontSize: 15, color: '#fff' }}>Add a job posting</Label>
            <Hint style={{ margin: '6px 0 12px' }}>Paste the whole posting from the job board or employer email: duties, requirements, location, pay, shifts.
              The fit analysis, tailored resume, cover letter and interview prep are all built from it.</Hint>
            <TextArea $h={260} value={draft.postingText} onChange={e => setDraft({ ...draft, postingText: e.target.value })} placeholder="Paste the job posting here…" />
            <Row $mt>
              <Field><Label>Job title (optional — detected)</Label><Input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} /></Field>
              <Field><Label>Company (optional — detected)</Label><Input value={draft.company} onChange={e => setDraft({ ...draft, company: e.target.value })} /></Field>
            </Row>
            <Row $mt>
              <Field $grow={2}><Label>Link to the posting (for reference)</Label><Input value={draft.sourceUrl} onChange={e => setDraft({ ...draft, sourceUrl: e.target.value })} placeholder="https://…" /></Field>
              <Field $min="150px"><Label>Documents language</Label>
                <Select value={draft.language} onChange={e => setDraft({ ...draft, language: e.target.value })}><option value="en">English</option><option value="fr">Français</option></Select>
              </Field>
            </Row>
            <Row $mt>
              <Btn onClick={create} disabled={busy}>{busy ? 'Adding & analysing…' : hasSavedResume ? 'Add & analyse fit' : 'Add posting'}</Btn>
              {(jobs || []).length > 0 && <Btn $variant="ghost" onClick={() => setAdding(false)}>Cancel</Btn>}
            </Row>
          </>
        ) : current ? (
          <JobDetail key={current.id} threadId={threadId} job={current} hasSavedResume={hasSavedResume} clientName={clientName}
            onChange={replaceJob} onDeleted={() => load()} onAskClient={onAskClient} onEmail={onEmail} />
        ) : (
          <PreviewEmpty>Add a job posting to start.</PreviewEmpty>
        )}
      </DetailCol>
    </Layout>
  );
}

// ============================================================================

function JobDetail({ threadId, job, hasSavedResume, onChange, onDeleted, onAskClient, onEmail }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [tab, setTab] = useState(job.fit ? 'fit' : 'posting');
  const [busy, setBusy] = useState('');
  const [meta, setMeta] = useState({ title: job.title, company: job.company, location: job.location, status: job.status, language: job.language });
  const base = `/resumes/case/${threadId}/jobs/${job.id}`;

  const put = async (body, quiet) => {
    try {
      const r = await api.put(base, body);
      onChange(r.data.data.job);
      if (!quiet) showToast('Saved', 'success');
      return true;
    } catch (e) { showToast(errMsg(e, 'Could not save'), 'error'); return false; }
  };

  const generate = async (step, label) => {
    setBusy(step);
    try {
      const r = await api.post(`${base}/${step}`);
      onChange(r.data.data.job);
      showToast(`${label} ready — review and edit it`, 'success');
    } catch (e) { if (!isTokenError(e)) showToast(errMsg(e, `${label} failed`), 'error'); }
    finally { setBusy(''); }
  };

  const remove = async () => {
    if (!(await confirm({ title: 'Delete this application?', message: 'The posting and everything generated for it are removed. Copies already saved to case files stay.', confirmText: 'Delete', type: 'danger' }))) return;
    try { await api.delete(base); showToast('Deleted', 'success'); onDeleted(); }
    catch (e) { showToast(errMsg(e, 'Could not delete'), 'error'); }
  };

  const metaDirty = ['title', 'company', 'location', 'status', 'language'].some(k => (meta[k] || '') !== (job[k] || ''));

  return (
    <>
      <Row style={{ alignItems: 'flex-end' }}>
        <Field $grow={2}><Label>Job title</Label><Input value={meta.title || ''} onChange={e => setMeta({ ...meta, title: e.target.value })} /></Field>
        <Field $grow={2}><Label>Company</Label><Input value={meta.company || ''} onChange={e => setMeta({ ...meta, company: e.target.value })} /></Field>
        <Field><Label>Location</Label><Input value={meta.location || ''} onChange={e => setMeta({ ...meta, location: e.target.value })} /></Field>
        <Field $min="140px"><Label>Status</Label>
          <Select value={meta.status} onChange={e => setMeta({ ...meta, status: e.target.value })}>{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select>
        </Field>
        <Field $min="110px"><Label>Language</Label>
          <Select value={meta.language} onChange={e => setMeta({ ...meta, language: e.target.value })}><option value="en">English</option><option value="fr">Français</option></Select>
        </Field>
        <Btn $sm onClick={() => put(meta)} disabled={!metaDirty}>Save</Btn>
        <IconBtn $danger title="Delete application" onClick={remove}>🗑</IconBtn>
      </Row>

      <SubTabs>
        {[['fit', 'Fit', !!job.fit], ['resume', 'Tailored resume', !!job.tailoredResume], ['cover', 'Cover letter', !!job.coverLetter],
          ['prep', 'Interview prep', !!job.interviewPrep], ['posting', 'Posting', true]].map(([k, l, done]) => (
          <SubTab key={k} $active={tab === k} onClick={() => setTab(k)}>{l}{done && k !== 'posting' && <span className="dot" />}</SubTab>
        ))}
        <span style={{ flex: 1 }} />
        <Btn $sm $variant="ghost" onClick={() => onEmail({ jobId: job.id })}>✉ Email documents…</Btn>
      </SubTabs>

      {tab === 'fit' && <FitView job={job} busy={busy === 'analyze'} disabled={!hasSavedResume}
        onAnalyze={() => generate('analyze', 'Fit analysis')}
        onAsk={(gaps) => onAskClient(gaps, job.id)} />}

      {tab === 'resume' && (
        <DocTab
          exists={!!job.tailoredResume} disabled={!hasSavedResume} busy={busy === 'tailor'}
          generateLabel="✨ Tailor the resume to this job" regenerateLabel="Re-tailor from the saved resume"
          onGenerate={() => generate('tailor', 'Tailored resume')}
          intro="Same facts as the client's saved resume — re-ordered and re-phrased so what this employer asks for comes first. Nothing is added."
          initial={job.tailoredResume} kind="resume" base={base} api={api} job={job}
          onSave={(v) => put({ tailoredResume: v })}
          renderEditor={(v, set) => <TailoredResumeEditor value={v} onChange={set} changes={job.tailoredChanges} />}
        />
      )}

      {tab === 'cover' && (
        <DocTab
          exists={!!job.coverLetter} disabled={!hasSavedResume} busy={busy === 'cover-letter'}
          generateLabel="✨ Write the cover letter" regenerateLabel="Re-write"
          onGenerate={() => generate('cover-letter', 'Cover letter')}
          intro="Built from the tailored resume when there is one, otherwise the saved resume."
          initial={job.coverLetter} kind="cover_letter" base={base} api={api} job={job}
          onSave={(v) => put({ coverLetter: v })}
          renderEditor={(v, set) => <CoverLetterEditor value={v} onChange={set} />}
        />
      )}

      {tab === 'prep' && (
        <DocTab
          exists={!!job.interviewPrep} disabled={!hasSavedResume} busy={busy === 'interview-prep'}
          generateLabel="✨ Prepare the client for the interview" regenerateLabel="Re-generate"
          onGenerate={() => generate('interview-prep', 'Interview prep')}
          intro="A document for the CLIENT: likely questions with answers built from their real experience, what to bring, and tips."
          initial={job.interviewPrep} kind="interview_prep" base={base} api={api} job={job}
          onSave={(v) => put({ interviewPrep: v })}
          renderEditor={(v, set) => <InterviewPrepEditor value={v} onChange={set} />}
          extraActions={<Btn $sm $variant="ghost" onClick={() => onEmail({ jobId: job.id, kinds: ['interview_prep'], toClient: true })}>✉ Send to client</Btn>}
        />
      )}

      {tab === 'posting' && <PostingView job={job} onSave={(postingText, sourceUrl) => put({ postingText, sourceUrl })} />}
    </>
  );
}

// ---- Fit -------------------------------------------------------------------

function FitView({ job, busy, disabled, onAnalyze, onAsk }) {
  const fit = job.fit;
  if (!fit) {
    return (
      <Banner>
        How well does the client match this posting — what matches, what's missing, and what to ask them?
        <div style={{ marginTop: 10 }}><Btn $variant="ai" onClick={onAnalyze} disabled={busy || disabled}>{busy ? 'Analysing…' : '✨ Analyse fit'}</Btn></div>
      </Banner>
    );
  }
  const v = VERDICT[fit.verdict] || VERDICT.partial;
  return (
    <>
      <Row style={{ alignItems: 'center', flexWrap: 'nowrap', gap: 16 }}>
        <Score $c={v.color} $v={fit.score}><span>{fit.score}</span></Score>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: v.color }}>{v.label}</div>
          <div style={{ fontSize: 13.5, color: '#c7cee0', lineHeight: 1.5, marginTop: 4 }}>{fit.summary}</div>
          <Hint style={{ marginTop: 4 }}>Analysed {fmtDateTime(fit.analyzedAt)} from the saved resume.</Hint>
        </div>
        <Btn $sm $variant="ghost" onClick={onAnalyze} disabled={busy || disabled}>{busy ? 'Analysing…' : 'Re-analyse'}</Btn>
      </Row>

      {fit.questionsToClarify?.length > 0 && (
        <Banner $tone="warn" style={{ marginTop: 14 }}>
          <b>Worth asking the client</b> — the resume is silent on these, they may simply not have mentioned them:
          <ul>{fit.questionsToClarify.map((q, i) => <li key={i}>{q}</li>)}</ul>
          <Btn $sm style={{ marginTop: 8 }} onClick={() => onAsk(fit.questionsToClarify)}>Ask the client</Btn>
        </Banner>
      )}

      <Section title="Matches" count={fit.matched?.length || 0}>
        {(fit.matched || []).map((m, i) => (
          <div key={i} style={{ margin: '8px 0', fontSize: 13 }}>
            <div style={{ color: '#86efac', fontWeight: 600 }}>✓ {m.requirement}</div>
            <div style={{ color: '#aeb6ca', paddingLeft: 16 }}>{m.evidence}</div>
          </div>
        ))}
      </Section>
      <Section title="Missing or unclear" count={fit.missing?.length || 0}>
        {(fit.missing || []).map((m, i) => (
          <div key={i} style={{ margin: '8px 0', fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#e6e9f0' }}>
              <span style={{ color: (SEVERITY[m.severity] || [])[1], fontSize: 11, fontWeight: 700, marginRight: 8 }}>{(SEVERITY[m.severity] || ['Important'])[0].toUpperCase()}</span>
              {m.requirement}
            </div>
            {m.suggestion && <div style={{ color: '#aeb6ca', paddingLeft: 16 }}>→ {m.suggestion}</div>}
          </div>
        ))}
      </Section>
      {(fit.strengths?.length > 0 || fit.concerns?.length > 0) && (
        <Section title="Strengths & concerns">
          {(fit.strengths || []).map((s, i) => <div key={`s${i}`} style={{ fontSize: 13, color: '#86efac', margin: '4px 0' }}>+ {s}</div>)}
          {(fit.concerns || []).map((s, i) => <div key={`c${i}`} style={{ fontSize: 13, color: '#fcd34d', margin: '4px 0' }}>– {s}</div>)}
        </Section>
      )}
    </>
  );
}

// ---- generic generated-document tab ------------------------------------------

function DocTab({ exists, disabled, busy, generateLabel, regenerateLabel, onGenerate, intro, initial, kind, base, api, job, onSave, renderEditor, extraActions }) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [value, setValue] = useState(initial);
  const [savedSnap, setSavedSnap] = useState(JSON.stringify(initial || null));
  const [saving, setSaving] = useState(false);

  // A regenerate/new job replaces the editor content.
  useEffect(() => { setValue(initial); setSavedSnap(JSON.stringify(initial || null)); }, [initial]);
  const dirty = JSON.stringify(value || null) !== savedSnap;

  const preview = usePdfPreview(
    async () => (await api.post(`${base}/preview/${kind}`, { content: value })).data.data.pdfBase64,
    [value], { enabled: !!value }
  );

  if (!exists) {
    return (
      <Banner>
        {intro}
        <div style={{ marginTop: 10 }}><Btn $variant="ai" onClick={onGenerate} disabled={busy || disabled}>{busy ? 'Generating…' : generateLabel}</Btn></div>
      </Banner>
    );
  }

  const save = async () => { setSaving(true); const ok = await onSave(value); if (ok) setSavedSnap(JSON.stringify(value || null)); setSaving(false); return ok; };
  const ensureSaved = async () => (dirty ? save() : true);
  const regenerate = async () => {
    if (!(await confirm({ title: regenerateLabel, message: 'Your edits on this document are replaced by a new AI version. Uses AI tokens.', confirmText: 'Re-generate', type: 'warning' }))) return;
    onGenerate();
  };
  const fileName = `${kind === 'resume' ? 'Resume' : kind === 'cover_letter' ? 'Cover Letter' : 'Interview Prep'} - ${job.company || job.title || 'Job'}.pdf`;

  return (
    <>
      <Row style={{ alignItems: 'center', marginBottom: 12 }}>
        <Hint style={{ flex: 1 }}>{intro}</Hint>
        <Btn $sm onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}</Btn>
        <Btn $sm $variant="ghost" onClick={async () => { if (await ensureSaved()) downloadPdf(api, `${base}/pdf/${kind}`, fileName).catch(() => showToast('Download failed', 'error')); }}>Download PDF</Btn>
        <Btn $sm $variant="green" onClick={async () => {
          if (!(await ensureSaved())) return;
          try { const r = await api.post(`${base}/save-to-files/${kind}`); showToast(`Saved “${r.data.data.savedFile.originalName}” to case files`, 'success'); }
          catch (e) { showToast(errMsg(e, 'Could not save to case files'), 'error'); }
        }}>Save to case files</Btn>
        {extraActions}
        <Btn $sm $variant="ghost" onClick={regenerate} disabled={busy}>{busy ? 'Generating…' : regenerateLabel}</Btn>
      </Row>
      <Split>
        <div>{value && renderEditor(value, setValue)}</div>
        <PreviewBox>
          <Row style={{ alignItems: 'center' }}>
            <Label>Preview</Label>{preview.loading && <Hint>rendering…</Hint>}
            <span style={{ flex: 1 }} /><Btn $sm $variant="ghost" onClick={preview.refresh}>Refresh</Btn>
          </Row>
          {preview.url ? <Iframe title="Document preview" src={preview.url} /> : <PreviewEmpty>{preview.error || 'Rendering…'}</PreviewEmpty>}
        </PreviewBox>
      </Split>
    </>
  );
}

// ---- editors -----------------------------------------------------------------

const withLists = (c) => ({
  ...c, contact: c?.contact || {}, summary: c?.summary || '',
  ...Object.fromEntries(['skills', 'experience', 'education', 'certifications', 'languages', 'volunteer', 'additional'].map(k => [k, c?.[k] || []])),
});

function TailoredResumeEditor({ value, onChange, changes }) {
  const v = useMemo(() => withLists(value), [value]);
  const set = (k) => (x) => onChange({ ...v, [k]: x });
  return (
    <>
      {changes?.length > 0 && (
        <Banner><b>What the AI changed for this job:</b><ul>{changes.map((c, i) => <li key={i}>{c}</li>)}</ul></Banner>
      )}
      <Section title="Headline">
        <Input value={v.contact.headline || ''} onChange={e => onChange({ ...v, contact: { ...v.contact, headline: e.target.value } })} />
        <Hint style={{ marginTop: 6 }}>Contact details come from the saved resume.</Hint>
      </Section>
      <Section title="Professional summary"><SummaryEditor value={v.summary} onChange={set('summary')} /></Section>
      <Section title="Skills"><SkillsEditor groups={v.skills} onChange={set('skills')} /></Section>
      <Section title="Professional experience" count={v.experience.length}><ExperienceEditor items={v.experience} onChange={set('experience')} /></Section>
      <Section title="Education" count={v.education.length} defaultOpen={false}><EducationEditor items={v.education} onChange={set('education')} /></Section>
      <Section title="Certifications & licences" count={v.certifications.length} defaultOpen={false}><CertificationsEditor items={v.certifications} onChange={set('certifications')} /></Section>
      <Section title="Languages" count={v.languages.length} defaultOpen={false}><LanguagesEditor items={v.languages} onChange={set('languages')} /></Section>
      <Section title="Volunteer experience" count={v.volunteer.length} defaultOpen={false}><ExperienceEditor volunteer items={v.volunteer} onChange={set('volunteer')} /></Section>
      <Section title="Custom sections" count={v.additional.length} defaultOpen={false}><AdditionalEditor items={v.additional} onChange={set('additional')} /></Section>
    </>
  );
}

function CoverLetterEditor({ value, onChange }) {
  const v = { paragraphs: [], ...value };
  const f = (k, label, ph) => (
    <Field><Label>{label}</Label><Input value={v[k] || ''} placeholder={ph} onChange={e => onChange({ ...v, [k]: e.target.value })} /></Field>
  );
  return (
    <>
      <Section title="Recipient">
        <Row>{f('recipientName', 'Name', 'Hiring Manager')}{f('recipientTitle', 'Title')}</Row>
        <Row $mt>{f('companyName', 'Company')}{f('companyAddress', 'Address / city')}</Row>
        <Row $mt>{f('jobTitle', 'Re: (job title)')}{f('greeting', 'Greeting', 'Dear Hiring Manager,')}</Row>
      </Section>
      <Section title="Letter" count={v.paragraphs.length}>
        {v.paragraphs.map((p, i) => (
          <Row key={i} style={{ marginBottom: 8, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
            <TextArea $h={90} value={p} onChange={e => onChange({ ...v, paragraphs: v.paragraphs.map((x, k) => (k === i ? e.target.value : x)) })} />
            <IconBtn title="Remove paragraph" $danger onClick={() => onChange({ ...v, paragraphs: v.paragraphs.filter((_, k) => k !== i) })}>×</IconBtn>
          </Row>
        ))}
        <Btn $sm $variant="ghost" onClick={() => onChange({ ...v, paragraphs: [...v.paragraphs, ''] })}>+ Add paragraph</Btn>
        <Row $mt>{f('closing', 'Closing', 'Sincerely,')}</Row>
      </Section>
    </>
  );
}

function InterviewPrepEditor({ value, onChange }) {
  const v = { questions: [], toBring: [], tips: [], ...value };
  const setQ = (i, p) => onChange({ ...v, questions: v.questions.map((q, k) => (k === i ? { ...q, ...p } : q)) });
  return (
    <>
      <Section title="Overview"><TextArea $h={60} value={v.overview || ''} onChange={e => onChange({ ...v, overview: e.target.value })} /></Section>
      <Section title="Questions & suggested answers" count={v.questions.length}>
        {v.questions.map((q, i) => (
          <Entry key={i}>
            <Row style={{ flexWrap: 'nowrap', alignItems: 'center' }}>
              <span style={{ color: '#8b93a7', fontSize: 12 }}>{i + 1}.</span>
              <Input value={q.question || ''} placeholder="Question" onChange={e => setQ(i, { question: e.target.value })} />
              <IconBtn title="Remove" $danger onClick={() => onChange({ ...v, questions: v.questions.filter((_, k) => k !== i) })}>×</IconBtn>
            </Row>
            <Row $mt><Field><Label>Why they ask</Label><Input value={q.why || ''} onChange={e => setQ(i, { why: e.target.value })} /></Field></Row>
            <Row $mt><Field><Label>Suggested answer</Label><TextArea $h={80} value={q.suggestedAnswer || ''} onChange={e => setQ(i, { suggestedAnswer: e.target.value })} /></Field></Row>
          </Entry>
        ))}
        <Btn $sm $variant="ghost" onClick={() => onChange({ ...v, questions: [...v.questions, { question: '', why: '', suggestedAnswer: '' }] })}>+ Add question</Btn>
      </Section>
      <Section title="What to bring" count={v.toBring.length}><BulletsEditor items={v.toBring} onChange={x => onChange({ ...v, toBring: x })} placeholder="Item" addLabel="Add item" /></Section>
      <Section title="Tips" count={v.tips.length}><BulletsEditor items={v.tips} onChange={x => onChange({ ...v, tips: x })} placeholder="Tip" addLabel="Add tip" /></Section>
    </>
  );
}

function PostingView({ job, onSave }) {
  const [text, setText] = useState(job.postingText);
  const [url, setUrl] = useState(job.sourceUrl || '');
  const dirty = text !== job.postingText || url !== (job.sourceUrl || '');
  return (
    <>
      <Row style={{ alignItems: 'flex-end' }}>
        <Field $grow={3}><Label>Link</Label><Input value={url} onChange={e => setUrl(e.target.value)} /></Field>
        {job.sourceUrl && <Btn $sm $variant="ghost" onClick={() => window.open(job.sourceUrl, '_blank', 'noopener')}>Open ↗</Btn>}
        <Btn $sm disabled={!dirty} onClick={() => onSave(text, url)}>Save</Btn>
      </Row>
      <Hint style={{ margin: '10px 0' }}>Edited the posting? Re-run the steps you need (Fit → Re-analyse, etc.) — existing documents are not changed automatically.</Hint>
      <TextArea $h={420} value={text} onChange={e => setText(e.target.value)} />
    </>
  );
}
